import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell } from "recharts";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { Box, Typography } from "@mui/material";
import { getSalesByPaymentMethods } from "../../../Services/authService";
import SectionLoader from "../../../Components/Common/SectionLoader";

const PaymentMethodBarChart = ({
  selectedLocations,
  dateRange,
  applyClicked,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const textColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const textColor1 =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;

  const colorMapping = {
    "Membership Card":
      mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500,
    "Debit Card":
      mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500,
    "Credit/Debit Card":
      mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500,
    "Apple Pay": mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500,
    PayPal: mode === "dark" ? DARKCOLORS.Info500 : LIGHTCOLORS.Info500,
  };

  const [data, setData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    const fetchData = async () => {
      try {
        const formatLocalDate = (date) => {
          if (!date) return null;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
        };

        // Apply it to your date range:
        const startDate = dateRange[0]?.startDate
          ? formatLocalDate(new Date(dateRange[0].startDate))
          : null;
        const endDate = dateRange[0]?.endDate
          ? formatLocalDate(new Date(dateRange[0].endDate))
          : null;

        const response = await getSalesByPaymentMethods(
          startDate,
          endDate,
          selectedLocations,
        );

        if (response && response.paymentMethods.length > 0) {
          const transformedData = response.paymentMethods.map((item) => ({
            name:
              item.type === "Membership Card"
                ? t("dashboard.nafWallet")
                : item.type === "Credit/Debit Card"
                  ? t("dashboard.creditDebitCard")
                  : item.type,
            value: item.amount,
            color: colorMapping[item.type] || LIGHTCOLORS.GreyScale30,
          }));
          setData(transformedData);
          setTotalAmount(response.totalAmount);
        } else {
          // Default gray slice when no data is available
          setData([
            {
              name: t("sales_analytics.NoData"),
              value: 0.0001,
              color: LIGHTCOLORS.GreyScale30,
            },
          ]);
          setTotalAmount(0);
        }
      } catch (err) {
        setError(t("sales_analytics.errorLoadingSalesData"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedLocations, applyClicked]); // Refetch when filters change

  return (
    <Box>
      <h3 className="subheading-sec-card" style={{ marginBottom: "1rem" }}>
        {t("sales_analytics.PaymentMethods")}
      </h3>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={280}
        >
          <SectionLoader />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <div
          className="payment-discount-sec"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px",
            width: "100%",
          }}
        >
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <PieChart width={280} height={280}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={140}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </div>

          {/* Right Side - Legend */}
          <div
            className="payment-discount-legend"
            style={{
              flex: 1,
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Payment Methods */}
            {data.map((entry, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <div
                  className="legend-sec"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: entry.color,
                      borderRadius: "50%",
                      marginRight: "10px",
                      flexShrink: 0,
                    }}
                  />

                  <span
                    className="popup-text"
                    style={{
                      textAlign: "left",
                      margin: 0,
                    }}
                  >
                    {entry.name}
                  </span>
                </div>

                <span
                  className="popup-text1"
                  style={{
                    textAlign: "right",
                    minWidth: "80px",
                  }}
                >
                  € {entry.value.toFixed(2)}
                </span>
              </div>
            ))}

            {/* Divider */}
            <div
              style={{
                width: "100%",
                height: "1px",
                backgroundColor: mode === "dark" ? "#555" : "#ccc",
                margin: "4px 0 35px",
              }}
            />

            {/* Total */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span
                className="popup-text"
                style={{
                  fontSize: "18px",
                  color: textColor,
                  lineHeight: "1",
                  margin: 0,
                }}
              >
                {t("sales_analytics.total")}
              </span>

              <span
                className="popup-text1"
                style={{
                  fontSize: "30px",
                  fontWeight: 500,
                  color: textColor1,
                  lineHeight: "1",
                  margin: 0,
                }}
              >
                € {totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default PaymentMethodBarChart;
